<template>
  <Section
    :width="width || 'medium'"
    class="kpb-images-text-section"
    :theme="theme"
    :class="[
      `kpb-images-text-section-layout--${(layoutComp || '').includes('vertical') ? 'vertical' : 'horizontal'}`,
      `kpb-images-text-section-layout--${layoutComp || 'default'}`,
      `kpb-images-text-section-theme--${theme || 'none'}`,
      { 'kpb-images-text-section-theme': theme },
      { 'kpb-images-text-section--single': isSingle },
      { 'kpb-images-text-section--disable-resposive-padding': !showResponsivePadding },
    ]"
    :style="[
      {
        '--kpb-images-text-section-gap': gap,
        '--kpb-images-text-section-padding': padding,
        '--kpb-images-text-section-default-columns': isSingle ? '1fr' : 'repeat(2, 1fr)',
      },
    ]"
  >
    <template v-if="(value.images || []).length">
      <Slider v-if="imageLayout === 'slider'" :data="value.images" class="kpb-images-text-section__slider" />
      <Gallery
        v-else
        class="kpb-images-text-section__images"
        :class="[
          `kpb-images-text-section__images--layout-${imageLayout}`,
          {
            'kpb-images-text-section__images--span-last': spanLast,
          },
          {
            'kpb-images-text-section__images--frame': (value['image-settings'] || '').includes('frame'),
            'kpb-images-text-section__images--round-edges': (value['image-settings'] || '').includes('round-edges'),
          },
        ]"
        :caption-visibility="captionVisibility"
        :caption-placement="captionPlacement"
        :caption-position="captionPosition"
        :data="{ images: value.images }"
      />
    </template>

    <div
      v-if="value.content"
      class="kpb-images-text-section__content"
      :class="[
        `kpb-images-text-section__content-size--${value['text-width'] || 'medium'}`,
        {
          'kpb-images-text-section__content--text-over': (value['image-settings'] || '').includes('text-over'),
        },
      ]"
    >
      <TextComponent
        :data="{ content: content }"
        :text-align="(value['text-settings'] || '').includes('center') ? 'center' : 'start'"
      />
    </div>
  </Section>
</template>

<script>
import TextComponent from './Text'
import Gallery from './Gallery'
import Slider from './Slider'
import Section from './Section'

/**
 * Component for rendering commonly used content sections
 */
export default {
  props: {
    data: Object,
    width: {
      type: String,
      default: 'medium',
      enums: ['full', 'large', 'medium', 'small'],
    },
    layout: {
      type: String,
      default: 'default',
      enums: [
        'default',
        'vertical-image-top',
        'vertical-image-bottom',
        'horizontal-image-left',
        'horizontal-image-right',
      ],
    },
    theme: {
      type: String,
      default: 'none',
      enums: ['default', 'custom', 'primary', 'secondary', 'black', 'white', 'none'],
    },
    gap: {
      type: String,
      default: '4rem',
    },
    padding: {
      type: String,
      default: '4rem',
    },
    showResponsivePadding: {
      type: Boolean,
      default: false,
    },
    captionVisibility: {
      type: String,
      default: 'visible',
      enums: ['hover', 'visible', 'hidden'],
    },
    captionPlacement: {
      type: String,
      default: 'inside',
      enums: ['inside', 'outside', 'center'],
    },
    captionPosition: {
      type: String,
      default: 'auto',
      enums: ['auto', 'below', 'aside'],
    },
  },
  computed: {
    value() {
      return this.$path('content', this.data) || []
    },
    layoutComp() {
      if (['default', null, ''].includes(this.layout) && (this.value.images || []).length > 1)
        return 'vertical-image-bottom'
      return this.layout
    },
    spanLast() {
      switch (this.imageLayout) {
        case 'full-width':
          return false
        case 'gallery':
          return (this.value.images || []).length % 2 !== 0
        case 'first-image-full-width':
        case 'first-image-span':
        case 'second-image-span':
          return (this.value.images || []).length % 2 === 0
        default:
          return (this.value.images || []).length % 2 !== 0
      }
    },
    isSingle() {
      return !(this.value.images || []).length || !this.value.content
    },
    imageLayout() {
      if ((this.value.images || []).length === 1) return 'none'
      return this.value['image-layout'] || ((this.value.images || []).length > 2 ? 'gallery' : 'none')
    },
    content() {
      const aTagRegex = /<a[^>]*>(.*?)<\/a>/g

      let value = this.value.content

      const quote =
        '<p class="kpb-images-text-section__content-quote"><svg width="58" height="32" viewBox="0 0 58 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.6904 0.477539H29.404L17.5095 31.7153H0.795883L12.6904 0.477539Z" fill="#1E3240" /><path d="M40.5465 0.477539H57.2601L45.3656 31.7153H28.6519L40.5465 0.477539Z" fill="#1E3240" /></svg></p>'

      if ((this.value['text-settings'] || []).includes('quote')) {
        value = quote + value
      }

      const arrowRight =
        '<svg class="kpb-images-text-section__content-anchor-arrow" aria-hidden="true" focusable="false" data-icon="arrow-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>'

      if ((this.value['text-settings'] || []).includes('arrow-on-links'))
        return value.replace(aTagRegex, val => {
          return val.includes('data-cta') ? val : val + arrowRight
        })

      return value
    },
  },
  components: {
    TextComponent,
    Gallery,
    Section,
    Slider,
  },
}
</script>

<style lang="scss">
.kpb-images-text-section {
  $textOverMargin: 11rem;

  $gap: var(--kpb-images-text-section-gap);
  $padding: var(--kpb-images-text-section-padding);
  $responsiveGap: 1rem;

  &:nth-child(1).kpb-images-text-section--theme-none {
    padding-top: 0;
  }
  .kpb-section__container {
    display: grid;
    grid-template-columns: var(--kpb-images-text-section-default-columns);
    gap: $gap;
    align-items: center;
    @include respond-below('tablet') {
      gap: 0;
    }
  }
  &--disable-resposive-padding {
    .kpb-section__container {
      @include respond-below('tablet') {
        padding-inline: 0 !important;
      }
      .kpb-images-text-section__content {
        padding: 2rem;
      }
    }
  }
  @include respond-below('tablet') {
    padding-block: 0 1rem !important;
  }

  &-layout {
    &--horizontal {
      @include respond-below('tablet') {
        .kpb-section__container {
          grid-template-columns: 1fr;
        }
      }
      .kpb-images-text-section__content {
        max-width: initial;
      }
      &-image-left {
        .kpb-images-text-section__content--text-over {
          @include respond-above('tablet') {
            margin: 4rem auto 1.5rem calc(-#{$textOverMargin});
          }
        }
      }
      &-image-right {
        direction: rtl;
        .kpb-images-text-section__content-anchor-arrow {
          flex-direction: row-reverse;
        }
        .kpb-images-text-section__content-quote,
        .kpb-text {
          direction: ltr;
        }
        .kvass-media-render-image__caption,
        .kpb-gallery__item-caption-wrapper {
          direction: ltr;
        }
        .kpb-images-text-section__content--text-over {
          @include respond-above('tablet') {
            margin: 4rem calc(-#{$textOverMargin}) 1.5rem auto;
          }
        }
      }
    }

    &--vertical {
      .kpb-section__container {
        display: flex;
      }
      &-image-top {
        .kpb-section__container {
          flex-direction: column;
        }
        .kpb-images-text-section__content--text-over {
          @include respond-above('tablet') {
            margin: calc(-#{$textOverMargin}) 0rem auto 0;
          }
        }
      }
      &-image-bottom {
        .kpb-section__container {
          flex-direction: column-reverse;
        }
        .kpb-images-text-section__content--text-over {
          @include respond-above('tablet') {
            margin: auto 0rem calc(-#{$textOverMargin}) 0;
          }
        }
      }
    }
  }

  &-theme {
    padding: $padding;
    @include respond-below('tablet') {
      padding: $padding 0;
    }

    &--none {
      border-color: var(--primary);
    }
  }

  &.kpb-section--width-full {
    padding: $padding 0;

    @include respond-above('tablet') {
      &.kpb-images-text-section-layout--horizontal-image-right {
        .kpb-images-text-section__content {
          padding-left: 2rem;
        }
      }

      &.kpb-images-text-section-layout--horizontal-image-left {
        .kpb-images-text-section__content {
          padding-right: 2rem;
        }
      }
    }

    @include respond-below('tablet') {
      .kpb-images-text-section__content {
        padding: 2rem;
      }
    }
  }

  &__content {
    @include respond-below('tablet') {
      padding: 1rem 0rem;
    }

    &-anchor-arrow {
      width: 15px;
      margin: 0 0.5em -0.15em 0.5em;
    }
    &-quote {
      svg path {
        fill: var(--primary);
      }
    }
    &-size {
      &--large {
        max-width: 1000px;
      }
      &--medium {
        max-width: 830px;
      }
      &--small {
        max-width: 630px;
      }
      &--full {
        max-width: 100%;
      }
    }

    &--text-over {
      background: white;
      padding: 2rem;
      position: relative;
      color: initial;
      box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
    }
  }

  &__slider {
    @include respond-below('tablet') {
      margin-top: 2rem;
    }
  }

  &__images {
    @include respond-below('tablet') {
      margin-top: 2rem;
    }

    width: 100%;
    &--span-last {
      .kpb-gallery__item:last-child {
        grid-column-end: span 2;
      }
    }
    &--layout {
      &-second-image-span {
        .kpb-gallery__item:nth-child(2) {
          grid-row-end: span 2;
        }
      }
      &-first-image-span {
        .kpb-gallery__item:nth-child(1) {
          grid-row-end: span 2;
        }
      }
      &-first-image-full-width {
        .kpb-gallery__item:nth-child(1) {
          grid-column-end: span 2;
        }
      }
      &-none {
        .kpb-gallery__item:nth-child(3n + 3) {
          grid-column-end: span 2;
        }
        .kpb-gallery__item:nth-child(n + 3) {
          grid-column-end: span 2;
        }
      }
      &-gallery {
        display: grid;
        .kpb-gallery__item-thumbnail {
          aspect-ratio: 16/9;
        }
      }
      &-full-width {
        grid-template-columns: 1fr !important;
      }
    }

    $borderWidth: var(--kpb-images-text-section-border-width, 3px);
    $galleryPadding: var(--kpb-images-text-section-gallery-padding, 0.4rem);

    &--frame {
      .kpb-gallery__item-thumbnail {
        border-width: $borderWidth;
        border-style: solid;
        padding: $galleryPadding;
      }
      .kpb-gallery__item-caption-wrapper {
        bottom: calc(#{$borderWidth} + #{$galleryPadding});
        margin: 0 calc(#{$borderWidth} + #{$galleryPadding});
      }

      &[data-kpb-caption-placement='center'] {
        .kpb-gallery__item-caption-wrapper {
          bottom: unset;

          max-height: calc(100% - #{$borderWidth} * 2 - #{$galleryPadding} * 2);
        }
      }
    }

    &--round-edges {
      $radius: var(--kpb-images-text-section-radius, 3rem);
      $responsiveRadius: var(--kpb-images-text-section-responsive-radius, 2rem);

      .kpb-gallery__item,
      .kpb-gallery__item-thumbnail {
        border-radius: $radius;
      }

      @include respond-below('tablet') {
        .kpb-gallery__item,
        .kpb-gallery__item-thumbnail {
          border-radius: $responsiveRadius;
        }
      }
      .kpb-gallery__item-caption-wrapper {
        height: 100px;
        display: inline-flex;
        justify-content: center;
        align-items: flex-end;
        text-align: center;
        border-radius: 0 0 $radius $radius;
        @include respond-below('tablet') {
          border-radius: 0 0 calc(#{$responsiveRadius} - #{$galleryPadding})
            calc(#{$responsiveRadius} - #{$galleryPadding});
        }
      }

      &.kpb-images-text-section__images--frame {
        .kpb-gallery__item-caption-wrapper {
          border-radius: 0 0 calc(#{$radius} - #{$galleryPadding} - #{$borderWidth})
            calc(#{$radius} - #{$galleryPadding} - #{$borderWidth});
          @include respond-below('tablet') {
            border-radius: 0 0 calc(#{$responsiveRadius} - #{$galleryPadding} - #{$borderWidth})
              calc(#{$responsiveRadius} - #{$galleryPadding} - #{$borderWidth});
          }
        }
      }

      &[data-kpb-caption-placement='inside'] {
        .kpb-gallery__item-caption-wrapper {
          padding: 0.5em 2em;
          @include respond-below('tablet') {
            padding: 0.5em 1em;
          }
        }

        .kpb-gallery__item-caption {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }

      &[data-kpb-caption-placement='outside'] {
        .kpb-gallery__item-caption-wrapper {
          height: unset;
        }
      }

      &[data-kpb-caption-placement='center'] {
        .kpb-gallery__item-caption-wrapper {
          border-radius: 0;
          height: unset;
        }
      }
    }

    &--round-edges,
    &--frame {
      @include respond-below('tablet') {
        .kpb-gallery__item {
          margin: 1rem 0.5rem 0rem !important;
        }
      }
    }
  }
}
</style>
